import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  Modal
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URI } from "../Common/data";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Carousel } from "react-bootstrap";
import { getUTCDateFromISTDateString } from "../Utils/dateUtils";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { renderTagWithIcon } from "./tagIconHelper";

const Location = () => {
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedSubLocation, setSelectedSubLocation] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedTheater, setSelectedTheater] = useState("");
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [currentVideoTitle, setCurrentVideoTitle] = useState("");
  const [currentVideoType, setCurrentVideoType] = useState("embed");
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showHeldSlotModal, setShowHeldSlotModal] = useState(false);
  const [heldSlotDetails, setHeldSlotDetails] = useState(null);
  const [heldSlotForm, setHeldSlotForm] = useState({
    name: "",
    mobile: "",
    email: ""
  });


  const fetchAllLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URI}/location`);
      if (res.status === 200) {
        setLocations(res.data?.data);
      }
    } catch (error) {
      toast.error("Error fetching locations");
    }
    setLoading(false);
  };


  const fetchAllSubLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URI}/sub-location`);
      if (res.status === 200) {
        setSubLocations(res.data?.data);
      }
    } catch (error) {
      toast.error("Error fetching sub-locations");
    }
    setLoading(false);
  };

  const fetchSlotAvailability = async () => {
    if (!selectedSubLocation || !selectedDate) return;

    setLoading(true);
    try {
      const formattedDate = getUTCDateFromISTDateString(selectedDate);
      const res = await axios.get(
        `${API_URI}/slot/withTheater?date=${formattedDate}&subLocationId=${selectedSubLocation}`
      );

      if (res.status === 200) {
        const data = res.data?.data;
        setTheaters(data);
        setSelectedTheater("");
        setSelectedSlot("");
        setShowSummaryModal(false);
      }
    } catch (error) {
      toast.error("Error fetching slot availability");
    }
    setLoading(false);
  };

  const handleTheaterSelect = (theater) => {
    setSelectedTheater(theater);
  };

  const handleSlotClick = (slot, theater) => {
    setSelectedTheater(theater);
    setSelectedSlot(slot._id);
  };

  const handleUnavailableSlotClick = (slot, theater, timeSlotConfig) => {
    setHeldSlotDetails({
      slot,
      theater,
      timeSlotConfig
    });
    setHeldSlotForm({
      name: "",
      mobile: "",
      email: ""
    });
    setShowHeldSlotModal(true);
  };

  const handleHeldSlotFormChange = (event) => {
    const { name, value } = event.target;
    setHeldSlotForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleHeldSlotSubmit = (event) => {
    event.preventDefault();

    const trimmedName = heldSlotForm.name.trim();
    const trimmedMobile = heldSlotForm.mobile.trim();
    const trimmedEmail = heldSlotForm.email.trim();
    const normalizedMobile = trimmedMobile.replace(/\D/g, "");

    if (!trimmedName || !trimmedMobile || !trimmedEmail) {
      return toast.error("Please fill in your name, mobile number, and email.");
    }

    if (normalizedMobile.length < 10) {
      return toast.error("Please enter a valid mobile number.");
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return toast.error("Please enter a valid email address.");
    }

    const slot = heldSlotDetails?.slot;
    const theater = heldSlotDetails?.theater;
    const slotLabel = slot
      ? `${formatSlotTime(slot.startTime)} - ${formatSlotTime(slot.endTime)}`
      : "Selected unavailable slot";
    const eventDate = selectedDate instanceof Date
      ? selectedDate.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric"
      })
      : "";

    const lead = {
      name: trimmedName,
      mobile: trimmedMobile,
      email: trimmedEmail,
      theaterName: theater?.theater?.name || "",
      theaterId: theater?.theater?._id || "",
      slotId: slot?._id || "",
      timeSlotId: slot?.timeSlotId || "",
      slot: slotLabel,
      date: eventDate,
      location: selectedLocationLabel,
      createdAt: new Date().toISOString()
    };

    try {
      const savedLeads = JSON.parse(localStorage.getItem("cineHeldSlotLeads") || "[]");
      localStorage.setItem("cineHeldSlotLeads", JSON.stringify([...savedLeads, lead]));
    } catch (error) {
      localStorage.setItem("cineHeldSlotLeads", JSON.stringify([lead]));
    }

    const message = [
      "Hi Cine Celebrate, I want updates if this slot opens up.",
      "",
      `Name: ${lead.name}`,
      `Mobile: ${lead.mobile}`,
      `Email: ${lead.email}`,
      `Theater: ${lead.theaterName || "Not selected"}`,
      `Slot: ${lead.slot}`,
      `Date: ${lead.date || "Not selected"}`,
      `Location: ${lead.location || "Not selected"}`
    ].join("\n");

    setShowHeldSlotModal(false);
    window.open(`https://wa.me/919643055509?text=${encodeURIComponent(message)}`, "_blank");
    return toast.success("Details saved. Opening WhatsApp now.");
  };

  const handleContinue = () => {
    if (!selectedTheater) return toast.error("Please select a theater.");
    if (!selectedSlot) return toast.error("Please select a slot.");
    setShowSummaryModal(false);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    navigate(
      `/slot-detail/${selectedTheater?.theater?._id}/${selectedTheater._id}/${selectedSlot}`
    );
  };

  const isMobile = window.innerWidth < 768;

  const handleImageClick = (images, index) => {
  setCurrentImages(images);
  setCurrentIndex(index);
  setShowImageModal(true);
};
  const handleClose = () => {
    setShowImageModal(false);
  };

  const getVideoConfig = (url) => {
    if (!url) return { type: "embed", url: "" };

    const normalizedUrl = url.trim();
    const isDirectVideoFile = /\.(mp4|webm|ogg)(\?.*)?$/i.test(normalizedUrl);
    if (isDirectVideoFile) {
      return { type: "direct", url: normalizedUrl };
    }

    try {
      const parsedUrl = new URL(normalizedUrl);
      const host = parsedUrl.hostname.replace("www.", "");
      const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
      let videoId = "";

      if (host === "youtu.be") {
        videoId = pathParts[0] || "";
      } else if (host.includes("youtube.com")) {
        if (pathParts[0] === "watch") {
          videoId = parsedUrl.searchParams.get("v") || "";
        } else if (["shorts", "live", "embed", "v"].includes(pathParts[0])) {
          videoId = pathParts[1] || "";
        } else {
          videoId = parsedUrl.searchParams.get("v") || pathParts[pathParts.length - 1] || "";
        }
      }

      if (videoId) {
        return {
          type: "embed",
          url: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`
        };
      }

      return { type: "embed", url: normalizedUrl };
    } catch (error) {
      return { type: "embed", url: normalizedUrl };
    }
  };

  const handleVideoOpen = (videoUrl, theaterName) => {
    const videoConfig = getVideoConfig(videoUrl);
    setCurrentVideoUrl(videoConfig.url);
    setCurrentVideoType(videoConfig.type);
    setCurrentVideoTitle(theaterName || "Theater video");
    setShowVideoModal(true);
  };

  const handleVideoClose = () => {
    setShowVideoModal(false);
    setCurrentVideoUrl("");
    setCurrentVideoTitle("");
    setCurrentVideoType("embed");
  };

  const showPreviousImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? currentImages.length - 1 : prevIndex - 1
    );
  };

  const showNextImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === currentImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const formatSlotTime = (time) => {
    if (!time) return "";
    const [rawHours, rawMinutes] = time.split(":");
    const hours = Number(rawHours);
    const minutes = Number(rawMinutes);
    const period = hours >= 12 ? "PM" : "AM";
    const normalizedHours = hours % 12 || 12;
    return `${normalizedHours}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  const formattedSelectedDate = selectedDate?.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric"
  });
  const selectedSlotDetails = selectedTheater?.slots?.find((slot) => slot._id === selectedSlot);
  const selectedTimeSlotConfig = selectedTheater?.theater?.timeSlots?.find(
    (timeSlot) => timeSlot._id === selectedSlotDetails?.timeSlotId
  );
  const selectedSlotLabel = selectedSlotDetails
    ? `${formatSlotTime(selectedSlotDetails.startTime)} - ${formatSlotTime(selectedSlotDetails.endTime)}`
    : "";
  const selectedCityName =
    locations.find((location) => location._id === selectedLocation)?.name || "";
  const selectedAreaName =
    subLocations.find((sub) => sub._id === selectedSubLocation)?.name || "";
  const selectedLocationLabel = [selectedAreaName, selectedCityName].filter(Boolean).join(", ");
  const isInitialLoading =
    loading &&
    locations.length === 0 &&
    subLocations.length === 0 &&
    theaters.length === 0;


  useEffect(() => {
    fetchAllLocations();
    fetchAllSubLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0 && subLocations.length > 0) {
      const firstLocation = locations[0]?._id;
      setSelectedLocation(firstLocation);

      const relatedSubLocations = subLocations.filter(
        (sub) => sub.locationId._id === firstLocation
      );

      if (relatedSubLocations.length > 0) {
        const firstSubLocation = relatedSubLocations[0]._id;
        setSelectedSubLocation(firstSubLocation);
      }
    }
  }, [locations, subLocations]);



  useEffect(() => {
    if (selectedDate && selectedSubLocation) {
      fetchSlotAvailability();
    }
  }, [selectedDate, selectedSubLocation]);

  useEffect(() => {
    if (!showImageModal || currentImages.length <= 1) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        setCurrentIndex((prevIndex) =>
          prevIndex === 0 ? currentImages.length - 1 : prevIndex - 1
        );
      }

      if (event.key === "ArrowRight") {
        setCurrentIndex((prevIndex) =>
          prevIndex === currentImages.length - 1 ? 0 : prevIndex + 1
        );
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showImageModal, currentImages.length]);

  if (isInitialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container className="book-now-shell" style={{ paddingBottom: "20px", paddingTop: "20px" }}>
      <div className="updated-booking">
        <div className="updated-booking-left">
          <Card className="booking-hero-card">
      <Card.Body>
        <Row className="align-items-start g-4">
          {/* Calendar */}
          <Col xs={12} lg={6} className="mb-3 mb-lg-0">
            <div className="booking-calendar-shell">
              <div className="booking-calendar-topbar">
                <div>
                  <p className="booking-calendar-eyebrow">Date picker</p>
                  <h4>Pick Your Event Date</h4>
                </div>
              </div>

              <DatePicker
                selected={selectedDate}
                onChange={(value) => setSelectedDate(value)}
                minDate={new Date()}
                inline
                calendarClassName="booking-modern-calendar"
                dayClassName={(date) =>
                  date.toDateString() === selectedDate?.toDateString()
                    ? "booking-modern-calendar__day--selected"
                    : ""
                }
                renderCustomHeader={({
                  date,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled
                }) => (
                  <div className="booking-modern-calendar__header">
                    <button
                      type="button"
                      className="booking-modern-calendar__nav"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                    >
                      <FiChevronLeft />
                    </button>
                    <div className="booking-modern-calendar__title">
                      {date.toLocaleDateString("en-IN", {
                        month: "long",
                        year: "numeric"
                      })}
                    </div>
                    <button
                      type="button"
                      className="booking-modern-calendar__nav"
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                )}
              />
            </div>
             </Col>
          <Col xs={12} lg={6}>
              <div className="booking-hero-sidecard">
              <p className="booking-hero-sidecard__eyebrow">Booking flow</p>
              <h4>Pick Your Date, Compare Options & Book Your Slot</h4>
              <div className="booking-hero-stats">
                <div className="booking-hero-stat">
                  <span>Selected</span>
                  <strong>{formattedSelectedDate}</strong>
                </div>
                <div className="booking-hero-stat">
                  <span>Available theaters</span>
                  <strong>{theaters.length}</strong>
                </div>
              </div>
              <p className="booking-hero-copy">
                Browse private screening spaces, compare slot pricing, and continue once your preferred theater and time are selected.
              </p>
              {loading && (
                <div className="booking-hero-copy" style={{ marginTop: "12px" }}>
                  Updating theaters for the selected date...
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
        {selectedDate && (
  <>
    <div className="updated-Theaters">
      {theaters.map((theater) => {
        const images = theater.theater.imageUrls || [];
        const sortedSlots = theater.slots || [];
        const lowestPriceSlot = theater?.theater?.timeSlots?.reduce((lowest, current) => {
          if (!lowest || current.price < lowest.price) return current;
          return lowest;
        }, null);
        return (
          <Card
            key={theater._id}
            className={`booking-theater-card ${
              selectedTheater?._id === theater._id ? "booking-theater-card--selected" : ""
            }`}
            onClick={() => handleTheaterSelect(theater)}
          >
            <div className="booking-theater-card__content">
              {/* Image Carousel (optimized to prevent flicker) */}
              <div className="booking-theater-card__media text-center">
                {images.length > 0 && (
                  <Carousel
                    className="booking-theater-carousel"
                    controls={images.length > 1}
                    indicators={false}
                    fade
                    interval={3000}
                    pause="hover"
                  >
                    {images.map((imgUrl, index) => (
                      <Carousel.Item key={`${theater._id}-${index}`}>
                        <img
                          src={imgUrl}
                          alt={`Slide ${index + 1}`}
                          className="booking-theater-card__image"
                          loading="lazy"
                          onClick={() => handleImageClick(images, index)}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
              )}
              </div>

              <div className="booking-theater-card__body">
              <div className="theater-card-topbar">
                <div>
                  <p className="booking-theater-card__eyebrow">Private theater</p>
                <h5>
                  {theater.theater.name}
                </h5>
                </div>

                {theater.theater.videolink && (
                  <Button
                    className="WatchVideo-btn"
                    variant="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVideoOpen(theater.theater.videolink, theater.theater.name);
                    }}
                  aria-label="Watch video"
                >
                  <span className="WatchVideo-btn-icon-wrap">
                    <span className="WatchVideo-btn-icon" aria-hidden="true" />
                  </span>
                  <span className="WatchVideo-btn-text">Watch video</span>
                </Button>
              )}
              </div>

              <div className="booking-theater-card__meta">
                <p>
                  {theater?.theater?.timeSlots?.[0]?.defaultPeople} guests included
                  {theater?.theater?.capacity ? ` (Maximum ${theater.theater.capacity})` : ""}
                </p>
                {lowestPriceSlot && (
                  <div className="booking-theater-card__price">
                    <span>Starts From</span>
                    <strong>₹{lowestPriceSlot.price}</strong>
                  </div>
                )}
              </div>

              {/* description */}
              {/* {theater?.theater?.description && (
                <p className="mt-3">{theater?.theater?.description}</p>
              )} */}
              {theater?.theater?.tags?.length > 0 && (
                <div className="booking-theater-card__tags mt-3">
                  {theater.theater.tags.map((tag) =>
                    renderTagWithIcon(tag)
                  )}
                </div>
              )}

              {/* Slots Below Carousel */}
              <div className="booking-slot-section">
                <div className="booking-slot-section__header">
                  <h6>Available slots</h6>
                  <span>{sortedSlots.length} options</span>
                </div>
              <Row className="g-3 mt-1">
                {sortedSlots.map((slot) => {
                  const currentTime = new Date();
                  const [hours, minutes] = slot.startTime.split(":");
                  const slotStartTime = new Date(selectedDate);
                  slotStartTime.setHours(parseInt(hours));
                  slotStartTime.setMinutes(parseInt(minutes));
                  slotStartTime.setSeconds(0);

                  const isPast = currentTime > slotStartTime;
                  const isSlotAvailable = slot.isAvailable && !isPast;
                  const findTimeSlot = theater?.theater?.timeSlots?.find((timeSlot) => timeSlot._id === slot.timeSlotId);
                  return (
                   <Col lg={4} md={6} sm={6} xs={6} key={slot._id}>

                      <div className="slot-btns-div">
                        <Button
                          onClick={(event) => {
                            event.stopPropagation();
                            if (isSlotAvailable) {
                              handleSlotClick(slot, theater);
                              return;
                            }
                            handleUnavailableSlotClick(slot, theater, findTimeSlot);
                          }}
                          className="slot-btns"
                          data-selected={selectedSlot === slot._id}
                          data-available={isSlotAvailable}
                        >
                          <span className="slot-btns__time">
                            {formatSlotTime(slot.startTime)} - {formatSlotTime(slot.endTime)}
                          </span>
                          {findTimeSlot && (
                            <div className="slot-btns__price">
                              <span className="slot-btns__strike">₹{findTimeSlot.strikePrice}</span>
                              <span>₹{findTimeSlot.price}</span>
                            </div>
                          )}
                        </Button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              </div>
              </div>
            </div>
          </Card>
        );
      })}
      {!theaters.length && (
        <div className="booking-empty-state">
          <h4>No theaters available for this date yet</h4>
          <p>Try another date and we will show matching private screening slots here.</p>
        </div>
      )}
    </div>
  </>
)}

        </div>
        {!isMobile && (
        <div className="updated-booking-right">
          <div className="booking-summry">
            <p className="booking-summary-kicker">Selection overview</p>
            <h3>Booking Summary</h3>

            <div className="selected-details">
              <p>Location</p>
              <h5>{selectedLocationLabel}</h5>
            </div>

            <div className="selected-details">
              <p>Theater</p>
              <h5>{selectedTheater?.theater?.name}</h5>
            </div>

            <div className="selected-details">
              <p>Slot</p>
              <h5>{selectedSlotLabel}</h5>
            </div>

            <div className="selected-details">
              <p>Date</p>
              <h5>
                {selectedDate instanceof Date
                  ? selectedDate.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  })
                  : new Date(selectedDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  })}
              </h5>
            </div>

            {selectedTimeSlotConfig && (
              <div className="selected-details">
                <p>Price</p>
                <h5>₹{selectedTimeSlotConfig.price}</h5>
              </div>
            )}

            <div className="booking-summary-note">
              Confirm your theater and slot selection to continue to the booking details step.
            </div>
            <button onClick={handleContinue}>Continue</button>
          </div>
        </div>
        )}
      </div>

      {isMobile && selectedTheater && selectedSlot && (
        <div className="booking-mobile-summarybar">
          <button
            type="button"
            className="booking-mobile-summarybar__button"
            onClick={() => setShowSummaryModal(true)}
          >
            <span className="booking-mobile-summarybar__copy">
              <strong>{selectedTheater?.theater?.name || "Selected theater"}</strong>
              <span>{selectedSlotLabel || "Slot selected"}</span>
            </span>
            <span className="booking-mobile-summarybar__cta">Review Booking</span>
          </button>
        </div>
      )}

      <Modal
        show={showImageModal}
        onHide={handleClose}
        centered
        size="xl"
        dialogClassName="booking-gallery-modal"
        contentClassName="booking-gallery-modal__content"
      >
        <Modal.Body className="booking-gallery-modal__body">
          {currentImages.length > 0 && (
            <div className="booking-gallery-viewer">
              <button
                type="button"
                className="booking-gallery-viewer__close"
                onClick={handleClose}
                aria-label="Close gallery"
              >
                Close
              </button>

              <div className="booking-gallery-viewer__header">
                <div>
                  <p className="booking-gallery-viewer__eyebrow">Theater preview</p>
                  <h4>Swipe through the space before you book</h4>
                </div>
                <div className="booking-gallery-viewer__counter">
                  {currentIndex + 1} / {currentImages.length}
                </div>
              </div>

              <div className="booking-gallery-viewer__stage">
                {currentImages.length > 1 && (
                  <button
                    type="button"
                    className="booking-gallery-viewer__nav booking-gallery-viewer__nav--prev"
                    onClick={showPreviousImage}
                    aria-label="View previous image"
                  >
                    <FiChevronLeft />
                  </button>
                )}

                <div className="booking-gallery-viewer__frame">
                  <img
                    src={currentImages[currentIndex]}
                    alt={`Theater preview ${currentIndex + 1}`}
                    className="booking-gallery-viewer__image"
                  />
                </div>

                {currentImages.length > 1 && (
                  <button
                    type="button"
                    className="booking-gallery-viewer__nav booking-gallery-viewer__nav--next"
                    onClick={showNextImage}
                    aria-label="View next image"
                  >
                    <FiChevronRight />
                  </button>
                )}
              </div>

              {currentImages.length > 1 && (
                <div className="booking-gallery-viewer__thumbs" aria-label="Gallery thumbnails">
                  {currentImages.map((imgUrl, index) => (
                    <button
                      type="button"
                      key={imgUrl + index}
                      className="booking-gallery-viewer__thumb"
                      data-active={currentIndex === index}
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img src={imgUrl} alt={`Thumbnail ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showSummaryModal}
        onHide={() => setShowSummaryModal(false)}
        centered
        dialogClassName="booking-summary-modal"
        contentClassName="booking-summary-modal__content"
      >
        <Modal.Body className="booking-summary-modal__body">
          <div className="booking-summry booking-summry--modal">
            <p className="booking-summary-kicker">Selection overview</p>
            <h3>Booking Summary</h3>

            <div className="selected-details">
              <p>Location</p>
              <h5>{selectedLocationLabel}</h5>
            </div>

            <div className="selected-details">
              <p>Theater</p>
              <h5>{selectedTheater?.theater?.name}</h5>
            </div>

            <div className="selected-details">
              <p>Slot</p>
              <h5>{selectedSlotLabel}</h5>
            </div>

            <div className="selected-details">
              <p>Date</p>
              <h5>
                {selectedDate instanceof Date
                  ? selectedDate.toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  })
                  : new Date(selectedDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric"
                  })}
              </h5>
            </div>

            {selectedTimeSlotConfig && (
              <div className="selected-details">
                <p>Price</p>
                <h5>₹{selectedTimeSlotConfig.price}</h5>
              </div>
            )}

            <div className="booking-summary-note">
              Confirm your theater and slot selection to continue to the booking details step.
            </div>
            <button onClick={handleContinue}>Continue</button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showHeldSlotModal}
        onHide={() => setShowHeldSlotModal(false)}
        centered
        dialogClassName="held-slot-modal"
        contentClassName="held-slot-modal__content"
      >
        <Modal.Body className="held-slot-modal__body">
          <button
            type="button"
            className="held-slot-modal__close"
            onClick={() => setShowHeldSlotModal(false)}
            aria-label="Close slot request form"
          >
            Close
          </button>

          <div className="held-slot-modal__header">
            <p className="held-slot-modal__kicker">Slot alert</p>
            <h3>Oops, someone might have booked it already.</h3>
            <p>
              You can consider booking another slot or another theater. Please hurry before someone else books it.
            </p>
            <p>
              Still want this particular slot? Drop your details below and we will inform you if any last minute cancellations happen.
            </p>
          </div>

          {heldSlotDetails?.slot && (
            <div className="held-slot-modal__summary">
              <span>{heldSlotDetails?.theater?.theater?.name || "Selected theater"}</span>
              <strong>
                {formatSlotTime(heldSlotDetails.slot.startTime)} - {formatSlotTime(heldSlotDetails.slot.endTime)}
              </strong>
              <small>{formattedSelectedDate}</small>
            </div>
          )}

          <form className="held-slot-form" onSubmit={handleHeldSlotSubmit}>
            <label>
              <span>Name</span>
              <input
                type="text"
                name="name"
                value={heldSlotForm.name}
                onChange={handleHeldSlotFormChange}
                placeholder="Your name"
              />
            </label>

            <label>
              <span>Mobile Number</span>
              <input
                type="tel"
                name="mobile"
                value={heldSlotForm.mobile}
                onChange={handleHeldSlotFormChange}
                placeholder="Your mobile number"
              />
            </label>

            <label>
              <span>Email</span>
              <input
                type="email"
                name="email"
                value={heldSlotForm.email}
                onChange={handleHeldSlotFormChange}
                placeholder="Your email address"
              />
            </label>

            <button type="submit">Send on WhatsApp</button>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showVideoModal}
        onHide={handleVideoClose}
        centered
        size="xl"
        dialogClassName="booking-video-modal"
        contentClassName="booking-video-modal__content"
      >
        <Modal.Header closeButton className="booking-video-modal__header">
          <Modal.Title>{currentVideoTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="booking-video-modal__body">
          {currentVideoUrl && (
            <div className="booking-video-modal__frame">
              {currentVideoType === "direct" ? (
                <video
                  src={currentVideoUrl}
                  title={currentVideoTitle || "Theater video"}
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <iframe
                  src={currentVideoUrl}
                  title={currentVideoTitle || "Theater video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

    </Container>
  );
};

export default Location;






 {/* 
          
          <h3>Select Location</h3>
          <div className="updated-section">
            <div className="updated-section-sub">
              <h4>Choose a city</h4>
              <Form.Group controlId="locationSelect">
                <Form.Select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="">Select Location</option>
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="updated-section-sub">
              <h4>Choose an area</h4>
              <Form.Group controlId="subLocationSelect">
                <Form.Select
                  value={selectedSubLocation}
                  onChange={(e) => setSelectedSubLocation(e.target.value)}
                  disabled={!selectedLocation}
                >
                  <option value="">Select Sub-Location</option>
                  {subLocations
                    .filter((sub) => sub.locationId._id === selectedLocation)
                    .map((subLocation) => (
                      <option key={subLocation._id} value={subLocation._id}>
                        {subLocation.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>
          <br />
          */}






// import React, { useState, useEffect } from "react";
// import {
//   Form,
//   Container,
//   Row,
//   Col,
//   Card,
//   Spinner,
//   Button,
//   Alert
// } from "react-bootstrap";
// import axios from "axios";
// import { toast } from "react-toastify";
// import { API_URI } from "../Common/data";
// import TheaterCard from "./TheaterCard";
// import { useNavigate } from "react-router-dom";
// import Calendar from "react-calendar";
// import "react-calendar/dist/Calendar.css";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import { Carousel } from "react-bootstrap";
// import {getUTCDateFromISTDateString} from '../Utils/dateUtils';

// const Location = () => {
//   const navigate = useNavigate();
//   const [locations, setLocations] = useState([]);
//   const [subLocations, setSubLocations] = useState([]);
//   const [filteredSubLocations, setFilteredSubLocations] = useState([]);
//   const [theaters, setTheaters] = useState([]);
//   const [slots, setSlots] = useState([]);
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [selectedSubLocation, setSelectedSubLocation] = useState("");
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [selectedSlot, setSelectedSlot] = useState("");
//   const [selectedTheater, setSelectedTheater] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [date, setDate] = useState(new Date());
//   const [theaterImageIndexes, setTheaterImageIndexes] = useState({});
//   const [selectedTimeSlotId, setSelectedTimeSlotId] = useState(null);

//   // console.log(selectedTheater);
//   console.log(selectedDate);
//   // Fetch all locations
//   const fetchAllLocations = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(`${API_URI}/location`);
//       if (res.status === 200) {
//         setLocations(res.data?.data);
//       }
//     } catch (error) {
//       toast.error("Error fetching locations");
//     }
//     setLoading(false);
//   };

//   // Fetch all sub-locations
//   const fetchAllSubLocations = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(`${API_URI}/sub-location`);
//       if (res.status === 200) {
//         setSubLocations(res.data?.data);
//       }
//     } catch (error) {
//       toast.error("Error fetching sub-locations");
//     }
//     setLoading(false);
//   };

//   // Fetch theaters with slot availability based on sub-location and date
//   const fetchSlotAvailability = async () => {
//     if (!selectedSubLocation || !selectedDate) return;

//     setLoading(true);
//     try {
//       // Format the date as YYYY-MM-DD for the API
//       const formattedDate = getUTCDateFromISTDateString(selectedDate);
//       // const formattedDate = selectedDate.toISOString().split("T")[0];
//       const res = await axios.get(
//         `${API_URI}/slot/withTheater?date=${formattedDate}&subLocationId=${selectedSubLocation}`
//       );

//       if (res.status === 200) {
//         const data = res.data?.data;
//         setTheaters(data);
//         if(!selectedTheater){
//           setSelectedTheater(data[0]);
//         }
//         if(!selectedTheater.slots){
//           setSlots(data[0].slots);
//           setSelectedSlot(data[0].slots[0]);
//         }

//         // If there was a previously selected theater, try to keep it selected
//         if (selectedTheater && selectedTheater._id) {
//           // console.log("Previously selected theater:", selectedTheater._id);
//           // console.log(
//           //   "Available theaters:",
//           //   data.map((t) => t._id)
//           // );

//           const previouslySelectedTheater = data.find((theater) => {
//             return theater.theater?._id === selectedTheater.theater?._id;
//           });

//           if (previouslySelectedTheater) {
//             // console.log(
//             //   "Found previously selected theater, keeping it selected"
//             // );
//             // Keep the same theater selected but update its slots
//             setSelectedTheater(previouslySelectedTheater);
//             setSlots(previouslySelectedTheater.slots || []);
//           } else {
//             console.log(
//               "Previously selected theater not available for this date, clearing selection"
//             );
//             // If the previously selected theater is not available for this date, clear selection
//             setSelectedTheater("");
//             setSlots([]);
//           }
//         }

//         // Always reset the selected slot when date changes
//         setSelectedSlot("");
//       }
//     } catch (error) {
//       console.log(error.message);
//       toast.error("Error fetching slot availability");
//     }
//     setLoading(false);
//   };

//   // console.log(theaters);

//   // Handle location change
//   const handleLocationChange = (e) => {
//     const locationId = e.target.value;
//     setSelectedLocation(locationId);
//     const filtered = subLocations.filter(
//       (sub) => sub.locationId._id === locationId
//     );
//     setFilteredSubLocations(filtered);
//     setSelectedSubLocation(""); // Reset sub-location selection
//     setTheaters([]); // Clear theaters
//     setSlots([]); // Clear slots
//     setSelectedTheater(""); // Clear selected theater
//     setSelectedSlot(""); // Clear selected slot
//   };

//   // Handle sub-location change
//   const handleSubLocationChange = (e) => {
//     setSelectedSubLocation(e.target.value);
//     setSelectedTheater(""); // Clear selected theater
//     setSelectedSlot(""); // Clear selected slot
//   };

//   const handleTheaterSelect = (theater) => {
//     console.log("Selecting theater:", theater._id, theater.theater.name);
//     setSelectedTheater(theater);
//     setSlots(theater.slots || []);
//     setSelectedSlot("");
//   };

//   const handleSlotClick = (val) => {
//     setSelectedSlot(val._id);
//     setSelectedTimeSlotId(val.timeSlotId);
//   };

//   // console.log(selectedSlot)
//   // console.log(theaters)
//   // console.log(slots)

//   const handleContinue = () => {
//     // const token = localStorage.getItem("cine_userToken");
//     // if (!token) return toast.error("Please Login First.");
//     if(!selectedTheater) return toast.error("Please select a theater.");
//     if(!selectedSlot) return toast.error("Please select a slot.");
//     if (selectedTheater && selectedSlot) {
//       navigate(
//         `/slot-detail/${selectedTheater?.theater?._id}/${selectedTheater._id}/${selectedSlot}`
//       );
//     }
//   };

//   // console.log(selectedTheater)

//   // Fetch data on sub-location or date change
//   useEffect(() => {
//     if (selectedSubLocation && selectedDate) {
//       fetchSlotAvailability();
//     }
//   }, [selectedSubLocation, selectedDate]);

//   // Debug useEffect to track selectedTheater changes
//   // useEffect(() => {
//   //   // console.log(
//   //   //   "Selected theater changed:",
//   //   //   selectedTheater?._id,
//   //   //   selectedTheater?.theater?.name
//   //   // );
//   // }, [selectedTheater]);

//   useEffect(() => {
//     fetchAllLocations();
//     fetchAllSubLocations();
//   }, []);

//   // Automatically set default location and sub-location when both are loaded
//   useEffect(() => {
//     if (locations.length > 0 && subLocations.length > 0) {
//       const firstLocation = locations[0]?._id;
//       setSelectedLocation(firstLocation);

//       const relatedSubLocations = subLocations.filter(
//         (sub) => sub.locationId._id === firstLocation
//       );
//       setFilteredSubLocations(relatedSubLocations);

//       if (relatedSubLocations.length > 0) {
//         const firstSubLocation = relatedSubLocations[0]._id;
//         setSelectedSubLocation(firstSubLocation);
//       }
//     }
//   }, [locations, subLocations]);

//   // Helper to get current image index for a theater
//   const getImageIndex = (theaterId) => theaterImageIndexes[theaterId] || 0;

//   // Handler to change image index for a theater
//   const handleImageChange = (theaterId, direction, imagesLength) => {
//     setTheaterImageIndexes((prev) => {
//       const current = prev[theaterId] || 0;
//       let next;
//       if (direction === "prev") {
//         next = current === 0 ? imagesLength - 1 : current - 1;
//       } else {
//         next = current === imagesLength - 1 ? 0 : current + 1;
//       }
//       return { ...prev, [theaterId]: next };
//     });
//   };

//   return (
//     <Container style={{ paddingBottom: "20px", paddingTop: "20px" }}>
//       <br />

//       <div className="updated-booking">
//         <div className="updated-booking-left">
//           <h3>Select Location</h3>
//           <div className="updated-section">
//             <div className="updated-section-sub">
//               <h4>Choose a city</h4>
//               <Form.Group controlId="locationSelect">
//                 <Form.Select
//                   value={selectedLocation}
//                   onChange={handleLocationChange}
//                 >
//                   <option value="">Select Location</option>
//                   {locations.map((location) => (
//                     <option key={location._id} value={location._id}>
//                       {location.name}
//                     </option>
//                   ))}
//                 </Form.Select>
//               </Form.Group>
//             </div>

//             <div className="updated-section-sub">
//               <h4>Choose an area in Delhi</h4>
//               <Form.Group controlId="subLocationSelect">
//                 {/* <Form.Label className="location-select">Select Sub-Location</Form.Label> */}
//                 <Form.Select
//                   value={selectedSubLocation}
//                   onChange={handleSubLocationChange}
//                   disabled={!selectedLocation}
//                 >
//                   <option value="">Select Sub-Location</option>
//                   {filteredSubLocations.map((subLocation) => (
//                     <option key={subLocation._id} value={subLocation._id}>
//                       {subLocation.name}
//                     </option>
//                   ))}
//                 </Form.Select>
//               </Form.Group>
//             </div>
//           </div>
//           <br />

//           <h3>Select Date & Time</h3>
//           <Container>
//             <Row>
//               {/* Calendar Section */}
//               <Col md={6} lg={6}>
//                 <Card style={{ border: "0" }}>
//                   <Card.Body>
//                     <Calendar
//                       onChange={(value) => {
//                         console.log(value)
//                         setSelectedDate(value);
//                       }}
//                       value={selectedDate}
//                       minDate={new Date()}
//                       tileClassName={({ date, view }) => {
//                         if (view === "month") {
//                           const isCurrentDate =
//                             date.toDateString() === new Date().toDateString();
//                           const isSelectedDate =
//                             selectedDate instanceof Date &&
//                             date.toDateString() === selectedDate.toDateString();

//                           if (isCurrentDate) return "current-date";
//                           if (isSelectedDate) return "selected-date";
//                         }
//                         return null;
//                       }}
//                       className="w-100"
//                     />
//                   </Card.Body>
//                 </Card>
//               </Col>

//               {/* Slots Section */}
//               <Col md={6} lg={6} className="mb-4">
//                 <Card className=" p-3 shadow-sm">
//                   <Card.Body>
//                     <h6 style={{ textAlign: "center" }}>Available Slots</h6>
//                     {loading && (
//                       <div className="text-center">
//                         <Spinner animation="border" variant="primary" />
//                       </div>
//                     )}
//                     {!loading && slots && slots.length > 0 ? (
//                       <Row className="g-2">
//                         {slots.map((slot) => {
//                           const currentTime = new Date();
//                           const [hours, minutes] = slot.startTime.split(":");
//                           const slotStartTime = new Date(selectedDate);
//                           slotStartTime.setHours(parseInt(hours));
//                           slotStartTime.setMinutes(parseInt(minutes));
//                           slotStartTime.setSeconds(0);
//                           slotStartTime.setMilliseconds(0);

//                           const isPast = currentTime > slotStartTime;
//                           const isSlotAvailable = slot.isAvailable && !isPast;
//                           return (
//                             <Col md={6} lg={4} key={slot._id}>
//                               <Card
//                                 className=" text-center shadow-sm"
//                                 // onClick={
//                                 //   slot.isAvailable
//                                 //     ? () => handleSlotClick(slot._id)
//                                 //     : null
//                                 // }
//                                 onClick={
//                                   isSlotAvailable
//                                     ? () =>{ 
//                                       handleSlotClick(slot)
//                                     }
//                                     : null
//                                 }
//                                 style={{
//                                   cursor: isSlotAvailable
//                                     ? "pointer"
//                                     : "not-allowed",
//                                   background:
//                                     selectedSlot === slot._id
//                                       ? "linear-gradient(90deg, #FFB284 0%, #E89897 100%)"
//                                       : "white",
//                                   color:
//                                     selectedSlot === slot._id
//                                       ? "white"
//                                       : "black",
//                                   border:
//                                     selectedSlot === slot._id
//                                       ? "none"
//                                       : "1px solid #ddd",
//                                   padding: "15px 0"
//                                 }}
//                               >
//                                 <Card.Text
//                                   className="fw-bold"
//                                   style={{ fontSize: "12px" }}
//                                 >
//                                   {slot.startTime} - {slot.endTime}
//                                 </Card.Text>
//                                 <Card.Text>
//                                   {isSlotAvailable
//                                     ? "Available"
//                                     : "Not Available"}
//                                 </Card.Text>
//                               </Card>
//                             </Col>
//                           );
//                         })}
//                       </Row>
//                     ) : (
//                       <p>No slots available</p>
//                     )}
//                   </Card.Body>
//                 </Card>
//               </Col>
//             </Row>
//           </Container>

//           <br />
//           <h3>Select Theater</h3>
//           <div>
//             <div className="updated-Theaters">
//               {/* {theaters.map((theater) => (
//                 <div
//                   className={`updated-Theater ${
//                     selectedTheater?._id === theater._id ? "bg-selected" : ""
//                   }`}
//                   onClick={() => handleTheaterSelect(theater)}
//                   key={theater._id}
//                 >
//                   <div className="updated-Theater-img">
//                     {theater.theater.imageUrls.length > 0 && (
//                       <Card.Img
//                         variant="top"
//                         src={theater.theater.imageUrls[0]}
//                         alt={theater.theater.name}
//                         className="theater-image"
//                         style={{
//                           width: "100px",
//                           objectFit: "cover",
//                           borderRadius: "5px"
//                         }}
//                       />
//                     )}
//                   </div>
//                   <div className="updated-Theater-text">
//                     <p>
//                       <b>{theater.theater.name}</b>
//                     </p>
//                     <p>{theater.theater.capacity} people</p>
//                   </div>
//                 </div>
//               ))} */}
//               {theaters.map((theater) => {
//                 const images = theater.theater.imageUrls;
//                 const imgIndex = getImageIndex(theater._id);

//                 const sortedSlots = theater.theater.timeSlots.sort(
//                   (a, b) => a.price - b.price
//                 );
//                 const bestSlot =
//                   sortedSlots.find((slot) => slot._id === selectedTimeSlotId) ||
//                   sortedSlots[0];

//                 if (!bestSlot) return null; // No slots

//                 const {
//                   price,
//                   strikePrice,
//                   maxPeople,
//                   additionalCostPerPerson
//                 } = bestSlot;
//                 const offerAmount = strikePrice - price;

//                 return (
//                   <div
//                     className={`updated-Theater ${
//                       selectedTheater?.theater?._id === theater?.theater?._id
//                         ? "bg-selected"
//                         : ""
//                     }`}
//                     onClick={() => handleTheaterSelect(theater)}
//                     key={theater._id + Math.random()}
//                     style={{
//                       border: "1px solid #f0a0a0",
//                       borderRadius: "10px",
//                       padding: "15px",
//                       marginBottom: "15px",
//                       backgroundColor:
//                         selectedTheater?.theater?._id === theater?.theater?._id
//                           ? "#ffe8e8"
//                           : "#fff",
//                       cursor: "pointer",
//                       boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
//                     }}
//                   >
//                     {/* Image Section */}
//                     <div
//                       className="updated-Theater-img"
//                       onClick={(e) => e.stopPropagation()}
//                     >
//                       {images.length > 0 && (
//                         <Carousel
//                           interval={1000}
//                           controls={images.length > 1}
//                           controls={false}
//                           indicators={false}
//                           fade
//                           style={{
//                             width: "120px",
//                             height: "100px",
//                             margin: "auto"
//                           }}
//                         >
//                           {images.map((imgUrl, index) => (
//                             <Carousel.Item key={index}>
//                               <img
//                                 src={imgUrl}
//                                 alt={`Slide ${index + 1}`}
//                                 style={{
//                                   width: "100px",
//                                   height: "100px",
//                                   objectFit: "cover",
//                                   borderRadius: "8px",
//                                   margin: "auto"
//                                 }}
//                               />
//                             </Carousel.Item>
//                           ))}
//                         </Carousel>
//                       )}
//                     </div>

//                     {/* Theater Info */}
//                     <div
//                       className="updated-Theater-text"
//                       style={{ marginTop: "10px" }}
//                     >
//                       <p
//                         style={{
//                           fontWeight: "bold",
//                           fontSize: "16px",
//                           marginBottom: "5px"
//                         }}
//                       >
//                         {theater.theater.name}
//                       </p>
//                       <p style={{ marginBottom: "6px" }}>
//                         {theater?.theater?.timeSlots[0]?.defaultPeople} people
//                       </p>

//                       <p style={{ margin: "4px 0" }}>
//                         <span
//                           style={{
//                             textDecoration: "line-through",
//                             marginRight: "5px"
//                           }}
//                         >
//                           ₹{strikePrice}
//                         </span>
//                         <span
//                           style={{
//                             fontWeight: "bold",
//                             fontSize: "15px"
//                           }}
//                         >
//                           ₹{price}
//                         </span>
//                       </p>

//                       {offerAmount > 0 && (
//                         <p
//                           style={{
//                             color: "#ca5052ff",
//                             fontSize: "14px",
//                             marginBottom: "4px"
//                           }}
//                         >
//                           Save ₹{offerAmount}!
//                         </p>
//                       )}

//                       <p style={{ fontSize: "14px", marginBottom: "3px" }}>
//                         Max People: {maxPeople}
//                       </p>
//                       {theater?.theater?.timeSlots[0]?.defaultPeople <
//                         maxPeople && (
//                         <p style={{ fontSize: "14px", color: "#555" }}>
//                           Additional Person: ₹{additionalCostPerPerson}
//                         </p>
//                       )}
//                     </div>
//                   </div>
//                 );
//               })}

//               {/* {theaters.map((theater) => {
//                 const images = theater.theater.imageUrls;
//                 const imgIndex = getImageIndex(theater._id);
//                 return (
//                   <div
//                     className={`updated-Theater ${
//                       selectedTheater?.theater?._id == theater?.theater?._id ? "bg-selected" : ""
//                     }`}
//                     onClick={() => handleTheaterSelect(theater)}
//                     key={theater._id+Math.random()}
//                   >
//                     <div className="updated-Theater-img" onClick={e => e.stopPropagation()}>
//                       {images.length > 0 && (
//                         <div style={{ display: "flex", alignItems: "center" }}>
//                           <Button
//                             variant="outline-secondary"
//                             size="sm"
//                             style={{ marginRight: "5px" }}
//                             onClick={() => handleImageChange(theater._id, "prev", images.length)}
//                             disabled={images.length < 2}
//                           >
//                             {"<"}
//                           </Button>
//                           <Card.Img
//                             variant="top"
//                             src={images[imgIndex]}
//                             alt={theater.theater.name}
//                             className="theater-image"
//                             style={{
//                               width: "100px",
//                               objectFit: "cover",
//                               borderRadius: "5px"
//                             }}
//                           />
//                           <Button
//                             variant="outline-secondary"
//                             size="sm"
//                             style={{ marginLeft: "5px" }}
//                             onClick={() => handleImageChange(theater._id, "next", images.length)}
//                             disabled={images.length < 2}
//                           >
//                             {">"}
//                           </Button>
//                         </div>
//                       )}
//                       {images.length > 1 && (
//                         <div style={{ textAlign: "center", fontSize: "12px", marginTop: "2px" }}>
//                           {imgIndex + 1} / {images.length}
//                         </div>
//                       )}
//                     </div>
//                     <div className="updated-Theater-text">
//                       <p>
//                         <b>{theater.theater.name}</b>
//                       </p>
//                       <p>{theater.theater.capacity} people</p>
//                     </div>
//                   </div>
//                 );
//               })} */}
//             </div>
//             {/* <Row>
//               {theaters.map((theater) => (
//                 <Col
//                   key={theater._id}
//                   md={6}
//                   lg={4}
//                   className={`theater-card mb-4 ${
//                     selectedTheater?._id === theater._id
//                       ? "selected-theater"
//                       : ""
//                   }`}
//                   onClick={() => handleTheaterSelect(theater)}
//                 >
//                   <Card className=" text-dark shadow-sm">
//                     {theater.theater.imageUrls.length > 0 && (
//                       <Card.Img
//                         variant="top"
//                         src={theater.theater.imageUrls[0]}
//                         alt={theater.theater.name}
//                         className="theater-image"
//                         style={{ height: "180px", objectFit: "cover" }}
//                       />
//                     )}
//                     <Card.Body>
//                       <Card.Title>{theater.theater.name}</Card.Title>
//                       <Card.Text>
//                         <strong>Capacity:</strong> {theater.theater.capacity}{" "}
//                         people
//                         <br />
//                         <strong>Cancellation:</strong>{" "}
//                         {theater.theater.cancellationPolicy}
//                       </Card.Text>
//                     </Card.Body>
//                   </Card>
//                 </Col>
//               ))}
//             </Row> */}
//           </div>
          
//         </div>

//         <div className="updated-booking-right">
//           <div className="booking-summry">
//             <h3>Booking Summary</h3>
//             <br />

//             <div className="selected-details">
//               <p>City</p>
//               <h5>
//                 {locations.find((location) => location._id === selectedLocation)
//                   ?.name || ""}
//               </h5>
//             </div>

//             <div className="selected-details">
//               <p>Area</p>
//               <h5>
//                 {filteredSubLocations.find(
//                   (sub) => sub._id === selectedSubLocation
//                 )?.name || ""}
//               </h5>
//             </div>

//             <div className="selected-details">
//               <p>Theater</p>
//               <h5>{selectedTheater?.theater?.name}</h5>
//             </div>

//             <div className="selected-details">
//               <p>Date</p>
//               <h5>
//                 {selectedDate instanceof Date
//                   ? selectedDate.toDateString()
//                   : new Date(selectedDate).toDateString()}
//               </h5>
//             </div>

//             {/* Debug section - remove this after testing */}
//             {/* <div className="selected-details" style={{ border: "1px solid #ddd", padding: "10px", marginTop: "10px", backgroundColor: "#f9f9f9" }}>
//               <p><strong>Debug Info:</strong></p>
//               <p>Selected Theater ID: {selectedTheater?.theater?._id || "None"}</p>
//               <p>Selected Theater Name: {selectedTheater?.theater?.name || "None"}</p>
//               <p>Available Theaters: {theaters.length}</p>
//               <p>Available Slots: {slots.length}</p>
//             </div> */}

//             <br />
//             <button onClick={handleContinue}>Continue</button>
//           </div>
//         </div>
//       </div>
//       <br />
//       {/* {loading && (
//         <div className="text-center">
//           <Spinner animation="border" variant="primary" />
//         </div>
//       )} */}
//       {/* Loading Spinner */}
//     </Container>
//   );
// };

// export default Location;

// {
//   /* <div>
// <br />
// <h3>Select Date & Time</h3>
// <Calendar
//   onChange={setSelectedDate}
//   value={selectedDate}
//   tileClassName={({ selectedDate, view }) =>
//     view === "month" && date.getDate() === new Date().getDate()
//       ? "selected-date"
//       : null
//   }
// />

// <div className="slots-section">
//   <h6>Available Slots</h6>
//   {slots && slots.length > 0 ? (
//     <div className="slots-container">
//       {slots.map((slot) => (
//         <div
//           key={slot._id}
//           className={`slot-box ${
//             slot.isAvailable ? "available-slot" : "unavailable-slot"
//           }`}
//           onClick={
//             slot?.isAvailable
//               ? () =>
//                   handleSlotClick(selectedTheater._id, slot?._id)
//               : null
//           }
//         >
//           <p className="slot-time">
//             {slot.startTime} - {slot.endTime}
//           </p>
//           {/* <p className="slot-time">
//           Price - {slot.price}
//         </p> */
// }
// //           <p className="slot-status">
// //             {slot.isAvailable ? "✅ Available" : "❌ Not Available"}
// //           </p>
// //         </div>
// //       ))}
// //     </div>
// //   ) : (
// //     <p>No slots available</p>
// //   )}
// // </div>
// // </div> */}

// {
//   /* Theaters Display */
// }
// {
//   /* {!loading && theaters && theaters?.length > 0 && (
//   <div>
//     <h3>Select Theater</h3>
//     <br />

//     <div className="theater-cards">
//       {theaters.map((theater) => (
//         <div key={theater._id}>
//           <TheaterCard theater={theater} />
//         </div>
//       ))}
//     </div>
//   </div>
// )} */
// }

// {
//   /* No Theaters Found */
// }
// {
//   /* {!loading &&
//   selectedSubLocation &&
//   selectedDate &&
//   theaters.length === 0 && (
//     <Alert variant="danger" className="text-center">
//       No theaters found for this date and sub-location.
//     </Alert>
//   )} */
// }
// {
//   /* <h1 className="text-center mb-4">Find Theaters by Location & Date</h1> */
// }
// {
//   /* <div className="location-box-div">
//         <div className="location-box">
//           <Form.Group controlId="locationSelect">
//             <div className="check-radio">
//               {locations.map((location) => (
//                 <Form.Check
//                   type="radio"
//                   key={location._id}
//                   label={location.name}
//                   name="location"
//                   value={location._id}
//                   checked={selectedLocation === location._id}
//                   onChange={handleLocationChange}
//                   className={`custom-radio ${
//                     selectedLocation === location._id ? "selected" : ""
//                   }`}
//                   style={{ paddingLeft: "0px" }}
//                 />
//               ))}
//             </div>
//           </Form.Group>
//           <br />

//           <Form.Group controlId="subLocationSelect">
//             <Form.Select
//               value={selectedSubLocation}
//               onChange={handleSubLocationChange}
//             >
//               <option value="">Select Sub-Location</option>
//               {filteredSubLocations.map((subLocation) => (
//                 <option key={subLocation._id} value={subLocation._id}>
//                   {subLocation.name}
//                 </option>
//               ))}
//             </Form.Select>
//           </Form.Group>

//           <br />

//           <Form.Group controlId="dateSelect">
//             <Form.Control
//               type="date"
//               value={selectedDate}
//               onChange={handleDateChange}
//             />
//           </Form.Group>
//         </div>
//       </div> */
// }
// {
//   /* Location, Sub-Location, and Date Selection */
// }
// {
//   /* <Row className="mb-4" >
//         <Col md={4}>
//           <Form.Group controlId="locationSelect">
//             <Form.Label className="location-select">Select Location</Form.Label>
//             <Form.Select value={selectedLocation} onChange={handleLocationChange}>
//               <option value="">Select Location</option>
//               {locations.map((location) => (
//                 <option key={location._id} value={location._id}>
//                   {location.name}
//                 </option>
//               ))}
//             </Form.Select>
//           </Form.Group>
//         </Col>

//         <Col md={4}>
//           <Form.Group controlId="subLocationSelect">
//             <Form.Label className="location-select">Select Sub-Location</Form.Label>
//             <Form.Select value={selectedSubLocation} onChange={handleSubLocationChange} disabled={!selectedLocation}>
//               <option value="">Select Sub-Location</option>
//               {filteredSubLocations.map((subLocation) => (
//                 <option key={subLocation._id} value={subLocation._id}>
//                   {subLocation.name}
//                 </option>
//               ))}
//             </Form.Select>
//           </Form.Group>
//         </Col>

//         <Col md={4}>
//           <Form.Group controlId="dateSelect">
//             <Form.Label className="location-select">Select Date</Form.Label>
//             <Form.Control type="date" value={selectedDate} onChange={handleDateChange} />
//           </Form.Group>
//         </Col>
//       </Row> */
// }
